<script setup>
import { humanReadableDuration, humanReadableTimestamp } from '@/composables/datetime'
import { getAge } from '@/composables/incidents/incidentTimeHelper'
import { useInstanceStore } from '@/store/backendInstances'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { writeToClipboard } from '@/composables/clipboard.ts'
import { useToast } from 'vue-toastification'

const props = defineProps({ incident: { type: Object, default: () => ({}) } })

const { t } = useI18n({})
const instanceStore = useInstanceStore()
const toast = useToast()

const showFailures = ref(false)

const componentType = computed(() => {
  return props.incident.identifier.componentType
})

const statusChipText = computed(() => {
  return t('incidents.healthCheck.component.' + componentType.value)
})

const statusIcon = computed(() => {
  if (componentType.value === 'camera') {
    return 'mdi-cctv'
  }
  if (componentType.value.startsWith('companionContainer')) {
    return 'mdi-server'
  }
  if (componentType.value === 'networkDevice') {
    return 'mdi-access-point-network'
  }
  return 'mdi-information'
})

async function copyComponentId(e) {
  const copyResult = await writeToClipboard(props.incident.identifier.componentId)
  if (copyResult) {
    toast.success(t('toast.copied_to_clipboard'))
  } else {
    toast.error(t('toast.copy_failed'))
  }
  e.stopPropagation()
}
</script>

<template>
  <v-layout-card>
    <div class="w-100 px-2 d-flex flex-row justify-space-between align-start flex-wrap">
      <div class="d-flex flex-column align-start">
        <v-card-title>
          <v-icon
            icon="mdi-bug"
            start
          />
          {{ $t('incidents.healthCheck.title') }}
        </v-card-title>
        <div class="mx-2 d-flex flex-row align-center ga-2 flex-wrap">
          <v-chip
            class="px-4"
            :prepend-icon="statusIcon"
            size="small"
            variant="tonal"
            color="primary"
          >
            {{ statusChipText }}
          </v-chip>
          <v-chip
            v-if="incident.messagePayload.latestFailures.failed > 0"
            class="px-4"
            prepend-icon="mdi-alert"
            size="small"
            color="error"
          >
            {{
              $t('incidents.healthCheck.failures.failedTestsMessage', {
                failed: incident.messagePayload.latestFailures.failed,
                outOf: incident.messagePayload.latestFailures.outOf,
              })
            }}
          </v-chip>
          <v-chip
            v-else
            class="px-4"
            prepend-icon="mdi-check"
            size="small"
            color="success"
          >
            {{ $t('incidents.healthCheck.failures.allTestsPassed') }}
          </v-chip>
        </div>
      </div>

      <div class="d-flex flex-row align-center ga-2">
        <slot name="jsonButton" />
        <slot name="commentButton" />
        <slot name="assignButton" />
      </div>
    </div>
    <v-card-text class="pt-0">
      <v-list>
        <v-list-item
          prepend-icon="mdi-state-machine"
          :title="$t('incidents.healthCheck.state.title')"
          :subtitle="$t(`incidents.healthCheck.state.${incident.state}`)"
        />

        <v-list-item
          prepend-icon="mdi-identifier"
          append-icon="mdi-content-copy"
          :title="$t('incidents.healthCheck.component.id')"
          :subtitle="incident.identifier.componentId"
          @click="copyComponentId"
        />

        <v-list-item
          v-if="componentType === 'camera'"
          prepend-icon="mdi-video"
          append-icon="mdi-open-in-new"
          :title="$t('incidents.healthCheck.titles.camera')"
          :subtitle="incident.messagePayload.prettyName"
          :to="`/instance/${instanceStore.selectedInstanceId}/live/${props.incident.identifier.componentId}`"
          target="_blank"
        />
        <v-list-item
          v-if="componentType !== 'camera' && incident.messagePayload.prettyName"
          prepend-icon="mdi-alphabetical-variant"
          :title="$t('incidents.healthCheck.titles.name')"
          :subtitle="incident.messagePayload.prettyName"
        />

        <v-list-item
          prepend-icon="mdi-clock-start"
          :title="$t('incidents.healthCheck.titles.firstDetection')"
          :subtitle="humanReadableTimestamp(incident.firstEventTimestamp)"
        />

        <v-list-item
          prepend-icon="mdi-clock-end"
          :title="$t('incidents.healthCheck.titles.lastDetection')"
          :subtitle="humanReadableTimestamp(incident.lastEventTimestamp)"
        />

        <v-list-item
          :title="$t('incidents.healthCheck.titles.duration')"
          prepend-icon="mdi-timelapse"
          :subtitle="humanReadableDuration(getAge(props.incident), undefined, ['d', 'h'], true)"
        />
      </v-list>

      <v-divider
        class="my-2"
        thickness="3"
      />

      <v-label class="mt-4">
        {{ $t('incidents.healthCheck.titles.lastFailures') }}
      </v-label>

      <v-list>
        <v-list-item
          prepend-icon="mdi-alert"
          :title="$t('incidents.healthCheck.failures.failed')"
          :subtitle="`${$t('incidents.healthCheck.failures.failedTestsMessage', {
            failed: incident.messagePayload.latestFailures.failed,
            outOf: incident.messagePayload.latestFailures.outOf,
          })}
          `"
        />

        <v-list-item
          v-if="incident.messagePayload.occurredFailures.length > 0"
          prepend-icon="mdi-eye"
          :append-icon="showFailures ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          :title="` ${
            showFailures ? $t('incidents.healthCheck.failures.hidefailureMessages') : $t('incidents.healthCheck.failures.showfailureMessages')
          }`"
          :subtitle="`
            ${incident.messagePayload.occurredFailures.length}
            ${$t('incidents.healthCheck.failures.failureMessages')}
          `"
          @click="showFailures = !showFailures"
        />
      </v-list>

      <v-expand-transition>
        <div
          v-show="showFailures"
          class="my-1"
        >
          <pre><code>{{
            JSON.stringify(
              incident.messagePayload.occurredFailures,
              null,
              2
            ) }}</code></pre>
        </div>
      </v-expand-transition>
    </v-card-text>
    <v-card-actions class="justify-end">
      <slot name="genericActions" />
    </v-card-actions>
  </v-layout-card>
</template>

<style scoped></style>
